import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/design';
import type { IAdminParticipantHub } from '../../api/participant-contracts';
import { ResponseStatusChip } from '../../components/responses-table';
import { LIVE } from '../../recycle-bin';
import { liveFirst } from './household';
import { surveyTabsOf } from './person-record';

/**
 * The Actions menu's "Move a response to the recycle bin…" (SR-490): the client recycles one
 * response after a search, from the record, and wanted it there rather than only in Lists. It is
 * **the survey strip's own press** (`RecyclePress`, SR-470's D3) — the same permission, dialog,
 * reason and refusals — reached from the menu: the press puts the reader on the survey's tab and
 * opens that tab's press, as "Decide routing…" opens the Team section's control. With more than
 * one survey whose current response is live, a dialog asks which first.
 */

/** A survey whose tab's response can be moved: its current response is live (`liveFirst`). */
export interface IRecyclableSurvey {
  code: string;
  name: string;
  cycle: string;
  status: IAdminParticipantHub['responses'][number]['status'];
}

/** The record's surveys whose current response is live, in the tabs' order. */
export function recyclableSurveys(hub: IAdminParticipantHub): IRecyclableSurvey[] {
  const id = hub.participant.participantId;
  return surveyTabsOf(hub).flatMap((tab) => {
    const current = liveFirst(
      hub.responses.filter(
        (row) => row.participant.participantId === id && row.survey.code === tab.code
      )
    )[0];
    return current && LIVE.has(current.status)
      ? [{ code: tab.code, name: tab.name, cycle: current.cycle.code, status: current.status }]
      : [];
  });
}

/**
 * The action: `start` from the menu — one survey goes to its tab at once, several open the
 * chooser — and the chooser itself, drawn by the page. `surveyPath` is a survey tab's address.
 */
export function useRecycleAction(
  surveys: readonly IRecyclableSurvey[],
  surveyPath: (code: string) => string,
  surveyCode: string | undefined
) {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const [choosing, setChoosing] = React.useState(false);
  const [pending, setPending] = React.useState<Nullable<string>>(null);

  // On the survey's tab, the strip's press is opened once it is drawn — within five seconds, and
  // only while the reader stays there: leaving the tab drops the press, so coming back to it later
  // never opens the dialog unasked.
  React.useEffect(() => {
    if (pending === null || surveyCode !== pending) return undefined;
    let tries = 0;
    const timer = window.setInterval(() => {
      const press = document.querySelector<HTMLButtonElement>(
        '[data-recycle-press] button:not(:disabled)'
      );
      tries += 1;
      if (press || tries > 100) {
        window.clearInterval(timer);
        setPending(null);
        press?.scrollIntoView({ block: 'center' });
        press?.click();
      }
    }, 50);
    return () => {
      window.clearInterval(timer);
      setPending(null);
    };
  }, [pending, surveyCode]);

  const go = (code: string) => {
    setChoosing(false);
    setPending(code);
    if (surveyCode !== code) navigate(`${surveyPath(code)}${search}`, { state });
  };
  const start = () => {
    if (surveys.length === 1) go(surveys[0].code);
    else if (surveys.length > 1) setChoosing(true);
  };

  const chooser = (
    <Dialog open={choosing} onOpenChange={setChoosing}>
      <DialogContent className="max-w-md" data-recycle-chooser="">
        <DialogHeader>
          <DialogTitle>Move a response to the recycle bin</DialogTitle>
          <DialogDescription>Choose the survey whose response to move.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ul className="grid gap-2">
            {surveys.map((survey) => (
              <li key={survey.code}>
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-between gap-3 py-2 text-left"
                  onClick={() => go(survey.code)}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{survey.name}</span>
                    <span className="block text-xs text-muted-foreground">{survey.cycle}</span>
                  </span>
                  <ResponseStatusChip status={survey.status} />
                </Button>
              </li>
            ))}
          </ul>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
  return { start, chooser };
}
